/**
 * Launch URLs for the EasyAI Studio (the App Builder / integration assistant
 * that lives at `/dashboard/playground` in the EasyAI app).
 *
 * There are two ways in, and the difference matters:
 *
 *   1. Direct link — `/dashboard/playground?platform=nutrition#nutrition_key=<LICENSE>`
 *      Carries the license, but only works if the browser already holds an
 *      EasyAI session.
 *   2. SSO handoff — `/api/v2/sso/passio?token=<HMAC>&redirect=<encoded studio path>`
 *      Always lands signed in, but cannot carry the license.
 *
 * The license may only travel in the fragment. Fragments are never transmitted
 * to the server, so the key stays out of nginx access logs, our app logs and
 * Referer headers; the Studio reads it once on mount and rewrites the URL to the
 * bare pathname so it does not linger in the address bar or history.
 *
 * A license must never be put on the SSO link. `redirect` is a query param on a
 * real server request, so a key there would be logged — and it would not arrive
 * anyway: the SSO bootstrap page ends with `window.location.replace(...)` to a
 * fully-specified URL, which drops any fragment, including one nested inside
 * `redirect`. Signed-out users get the wizard's license step instead.
 */

/** Preselects the Studio mode and skips the wizard's picker step. */
export type StudioPlatform = "nutrition" | "easyai" | "sdk";

export type StudioMode = "chat" | "app_builder";

export interface StudioTarget {
  platform?: StudioPlatform;
  /**
   * Optional. `platform=nutrition` already implies `app_builder`. `platform=sdk`
   * does not force a mode, because SDK knowledge loads in Chat as well as App
   * Builder, so it has to be asked for explicitly.
   */
  mode?: StudioMode;
  /** 1 forces the wizard open with the picker shown; 0 suppresses it. */
  wizard?: 0 | 1;
  /**
   * Only meaningful with `platform: "sdk"`. Preselects the SDK and seeds the
   * builder target languages. Accepts `ios` | `android` | `react_native` |
   * `flutter` and their documented aliases. An unrecognised value does not
   * error; it just leaves the SDK unselected so the wizard asks.
   */
  sdkPlatform?: string;
}

export const STUDIO_PLAYGROUND_PATH = "/dashboard/playground";

export type StudioIntent = "newApp" | "continueApp" | "askApi" | "sdkHelp";

export const STUDIO_INTENTS: Record<StudioIntent, StudioTarget> = {
  // App Builder with the wizard walking through a first build. `nutrition`
  // forces App Builder + nutrition_ai and skips the picker, so no explicit
  // mode or wizard flag is needed.
  newApp: { platform: "nutrition" },
  // Straight into the playground so an in-progress app can be picked back up.
  continueApp: { platform: "nutrition", wizard: 0 },
  // "How does this route work" questions. Deliberately not `platform=nutrition`:
  // that forces App Builder, and NutritionAI is ignored in Chat mode anyway.
  askApi: { platform: "easyai", mode: "chat" },
  // Mobile SDK help. Unlike `nutrition`, `platform=sdk` does NOT force a mode,
  // so chat is requested explicitly. The wizard is left on deliberately: its
  // SDK branch is where users meet proxy mode, the v2 to v3 break, and the
  // pinnable API reference pages. Add `wizard: 0` to drop straight into chat.
  sdkHelp: { platform: "sdk", mode: "chat" },
};

/**
 * Whether the Studio can answer mobile SDK questions. It can, so this now only
 * governs retiring the embedded SDK chatbot.
 *
 * The embed answers for anyone who loads the page, while the Studio needs an
 * EasyAI session, so the chatbot stays as the fallback for customers SSO cannot
 * sign in. Delete the embed, this flag, and the fallback branch in
 * `openSdkAssistant` once the fallback is provably unused — that also retires
 * the plaintext EasyAI API key currently shipped in the bundle via the iframe
 * src.
 */
export const STUDIO_SUPPORTS_SDK = true as boolean;

const buildStudioQuery = (target: StudioTarget): string => {
  const params = new URLSearchParams();
  if (target.platform) params.set("platform", target.platform);
  if (target.mode) params.set("mode", target.mode);
  if (target.wizard !== undefined) params.set("wizard", String(target.wizard));
  if (target.sdkPlatform) params.set("sdk_platform", target.sdkPlatform);
  const query = params.toString();
  return query ? `?${query}` : "";
};

/** Host-relative Studio URL, safe to hand to the SSO `redirect` param. */
export const buildStudioPath = (target: StudioTarget): string =>
  `${STUDIO_PLAYGROUND_PATH}${buildStudioQuery(target)}`;

const trimBase = (baseUrl: string): string =>
  String(baseUrl || "")
    .trim()
    .replace(/\/+$/, "");

/**
 * Absolute Studio URL with the license in the fragment. Requires the browser to
 * already hold an EasyAI session.
 */
export const buildStudioDirectUrl = (
  baseUrl: string,
  target: StudioTarget,
  license?: string | null
): string => {
  const url = `${trimBase(baseUrl)}${buildStudioPath(target)}`;
  const key = String(license ?? "").trim();
  return key ? `${url}#nutrition_key=${encodeURIComponent(key)}` : url;
};

/**
 * Adds a `redirect` back into the Studio onto an SSO URL returned by
 * `GET /accounts/products/unified/easyai/sso-url`.
 *
 * Appended as a raw string rather than through URLSearchParams so the signed
 * `token` is passed along byte for byte. Never carries the license.
 */
export const withStudioRedirect = (
  ssoUrl: string,
  target: StudioTarget
): string => {
  const [base] = String(ssoUrl || "").split("#");
  if (!base) return base;
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}redirect=${encodeURIComponent(
    buildStudioPath(target)
  )}`;
};
